import { Col, Row, Transfer, TreeSelect } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { PartialMarkup } from 'domains/markupDomain';
import { Organization } from 'domains/orgDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { cloneDeep, keyBy, union } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listToTree } from 'utils/misc';
import { PLATFORM_ORG_ID } from 'domains/orgDomain';

type TreeNodeOrganization = Organization & {
  value: Organization['id'];
  title: Organization['name'];
  children?: TreeNodeOrganization[];
};

const ShippingTransfer = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const shippingMarkup = useAppSelector(
    (state) => state.markup.curShippingMarkup
  );
  const [filterOrgId, setFilterOrgId] = useState<number>(PLATFORM_ORG_ID);
  const withMarkupCarriers: CarrierTypeEnum[] = useMemo(
    () =>
      shippingMarkup?.orgShippingFilters?.find((f) => f.orgId === filterOrgId)
        ?.carrierTypes || [],
    [filterOrgId, shippingMarkup?.orgShippingFilters]
  );
  const noMarkupCarriers: CarrierTypeEnum[] = useAppSelector(
    (state) => state.markup.noShippingMarkupCarriers
  );
  useEffect(() => {
    dispatch.markup.getNoRevshopShippingMarkupCarriers(filterOrgId);
    setTargetKeys(withMarkupCarriers);
  }, [dispatch.markup, filterOrgId, withMarkupCarriers]);

  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  const loading = useAppSelector(
    (state) =>
      state.loading.effects.markup.getRevshopShippingMarkups ||
      state.loading.effects.markup.getNoRevshopShippingMarkupCarriers
  );

  const sourceKeyedCarriers = union(noMarkupCarriers, withMarkupCarriers).map(
    (c) => ({ key: c.toString(), title: c.toString() })
  );

  // org selection
  const fullOrgList = useAppSelector((state) => state?.org?.list || []);
  const treeData: TreeNodeOrganization[] = useMemo(() => {
    const treeNodeList: TreeNodeOrganization[] = fullOrgList.map((o) => ({
      ...o,
      value: o.id,
      title: o.name,
      children: undefined,
    }));
    const orgMap = keyBy<TreeNodeOrganization>(treeNodeList, 'id');

    return listToTree<TreeNodeOrganization>(treeNodeList, orgMap);
  }, [fullOrgList]);

  const onOrgChange = (orgId: number) => {
    setTargetKeys([]);
    setFilterOrgId(orgId);
  };

  const onSave = () => {
    const updated: PartialMarkup = cloneDeep(shippingMarkup!);
    const orgShippingFilter = updated.orgShippingFilters?.find(
      (f) => f.orgId === filterOrgId
    );
    if (Boolean(orgShippingFilter)) {
      orgShippingFilter!.carrierTypes = targetKeys.map(
        (k) => CarrierTypeEnum[k as keyof typeof CarrierTypeEnum]
      );
    } else {
      if (updated.orgShippingFilters === null) {
        updated.orgShippingFilters = [];
      }
      updated.orgShippingFilters?.push({
        orgId: filterOrgId,
        carrierTypes: targetKeys.map(
          (k) => CarrierTypeEnum[k as keyof typeof CarrierTypeEnum]
        ),
      });
    }
    dispatch.markup.saveMarkup(updated);
  };

  return (
    <RevxSpin spinning={loading}>
      <TreeSelect
        showSearch
        style={{ width: 200 }}
        dropdownMatchSelectWidth={300}
        value={filterOrgId}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
        treeData={treeData}
        treeDefaultExpandAll
        onChange={onOrgChange}
        treeNodeFilterProp="title"
        placeholder="Select organization"
      />
      <Transfer
        titles={[
          t('platform.markup.labels.noMarkupCarriers'),
          t('platform.markup.labels.appliedCarriers'),
        ]}
        style={{ marginTop: 4, marginBottom: 16 }}
        showSelectAll={false}
        dataSource={sourceKeyedCarriers}
        targetKeys={targetKeys}
        onChange={setTargetKeys}
        render={(item) => item.title}
      />
      <Row>
        <Col>
          <RevxButton key="save" onClick={onSave} type="primary">
            {t('common.save')}
          </RevxButton>
        </Col>
      </Row>
    </RevxSpin>
  );
};

export default ShippingTransfer;
